/**
 * Date helpers — Singapore (Asia/Singapore) calendar + en-SG display labels.
 * Never use `toISOString().split('T')[0]` for "today" or date-only fields (UTC shifts before 08:00 SGT).
 */

/** Teleconsult queue / visit calendar date in Singapore (matches API VisitDate). */
export const TELECONSULT_QUEUE_TIME_ZONE = 'Asia/Singapore';

const SINGAPORE_TZ = TELECONSULT_QUEUE_TIME_ZONE;

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Extract YYYY-MM-DD from an ISO date or datetime without UTC day-shift.
 * Prefer this over `new Date(...).toISOString().split('T')[0]`.
 */
export function toDateOnlyISO(date: Date | string): string {
  if (typeof date === 'string') {
    const m = date.trim().match(DATE_ONLY_RE);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleDateString('en-CA', { timeZone: SINGAPORE_TZ });
}

/** Parse a calendar date for en-SG labels without UTC midnight shift. */
function toLocalCalendarDate(date: Date | string): Date {
  if (typeof date === 'string') {
    const m = date.trim().match(DATE_ONLY_RE);
    if (m) {
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }
  }
  return typeof date === 'string' ? new Date(date) : date;
}

/**
 * Formats a date-only value for UI labels: e.g. "15 Mar 1990" (en-SG).
 */
export function formatDate(date: Date | string): string {
  const dateObj = toLocalCalendarDate(date);
  if (Number.isNaN(dateObj.getTime())) return typeof date === 'string' ? date : '';
  return dateObj.toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Formats a date-only value with 2-digit day: e.g. "15 Mar 1990" (en-SG).
 */
export function formatDateShort(date: Date | string): string {
  const dateObj = toLocalCalendarDate(date);
  if (Number.isNaN(dateObj.getTime())) return typeof date === 'string' ? date : '';
  return dateObj.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Alias — all UI date labels use en-SG. */
export const formatDateSG = formatDate;

/** Long weekday label, e.g. "Tuesday, 14 July 2026". */
export function formatLongDateSG(date: Date | string): string {
  const dateObj = toLocalCalendarDate(date);
  if (Number.isNaN(dateObj.getTime())) return typeof date === 'string' ? date : '';
  return dateObj.toLocaleDateString('en-SG', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Parse API timestamps — stored/sent as UTC (often without a Z suffix from SQL/JSON).
 * Chat message `ts` is Unix epoch milliseconds.
 */
export function parseApiUtcDate(date: Date | string | number): Date {
  if (date instanceof Date) return date;
  if (typeof date === 'number' && Number.isFinite(date)) return new Date(date);
  const s = String(date).trim();
  if (!s) return new Date(NaN);
  if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) {
    return new Date(s);
  }
  return new Date(`${s}Z`);
}

/** Date + time in Singapore (UTC+8), e.g. for chat started/closed. */
export function formatDateTimeSG(date: Date | string | number): string {
  return parseApiUtcDate(date).toLocaleString('en-SG', {
    timeZone: SINGAPORE_TZ,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/** Time only in Singapore (UTC+8), e.g. for chat message bubbles. */
export function formatTimeSG(date: Date | string | number): string {
  return parseApiUtcDate(date).toLocaleTimeString('en-SG', {
    timeZone: SINGAPORE_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Today's calendar date as YYYY-MM-DD in Singapore time (not UTC).
 * Using UTC (`toISOString`) shows yesterday until 8 AM SGT.
 */
export function getTodayISO(timeZone: string = TELECONSULT_QUEUE_TIME_ZONE): string {
  return new Date().toLocaleDateString('en-CA', { timeZone });
}

/**
 * @deprecated Use {@link toDateOnlyISO} — this name misleads (not a full ISO datetime).
 */
export function toISOString(date: Date | string): string {
  return toDateOnlyISO(date);
}
